import React, { useState } from 'react';
import { API_BASE } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpNeeded, setOtpNeeded] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpMsg, setOtpMsg] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const isProd = process.env.NODE_ENV === 'production';
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [apiBaseInput, setApiBaseInput] = useState<string>(() => {
    try {
      const override = localStorage.getItem('API_BASE_OVERRIDE');
      return (override || API_BASE);
    } catch {
      return API_BASE;
    }
  });

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOtpNeeded(false);
    setOtpMsg('');

    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(`${API_BASE}/token`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Login successful:', data); // Debug log
        
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        login(data.access_token, data.refresh_token, data.user);
        
        console.log('Redirecting to dashboard...'); // Debug log
        // Redirect to dashboard after successful login
        navigate('/dashboard');
      } else {
        let errorData: any = {};
        try { errorData = await response.json(); } catch { /* ignore */ }
        if (response.status === 403 && (errorData?.detail || '').toLowerCase().includes('email not verified')) {
          setOtpNeeded(true);
          setOtpMsg('Email not verified. Enter the 6-digit code sent to your email.');
          setError('');
        } else {
          setError(errorData?.detail || 'Login failed');
        }
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      setOtpMsg('Please enter the verification code.');
      return;
    }
    setOtpLoading(true);
    setOtpMsg('');
    try {
      const resp = await fetch(`${API_BASE}/signup/verify_email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username_or_email: username, code: otpCode })
      });
      if (resp.ok) {
        setOtpMsg('Email verified. Signing you in...');
        // Retry login
        const fd = new FormData();
        fd.append('username', username);
        fd.append('password', password);
        const loginResp = await fetch(`${API_BASE}/token`, { method: 'POST', body: fd });
        if (loginResp.ok) {
          const data = await loginResp.json();
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('refreshToken', data.refresh_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          login(data.access_token, data.refresh_token, data.user);
          navigate('/dashboard');
          return;
        }
        const err = await loginResp.json().catch(() => ({}));
        setError((err as any)?.detail || 'Login failed after verification. Please try again.');
        setOtpNeeded(false);
      } else {
        const ed = await resp.json().catch(() => ({}));
        setOtpMsg((ed as any)?.detail || 'Verification failed. Please check the code.');
      }
    } catch {
      setOtpMsg('Network error. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpMsg('');
    try {
      const resp = await fetch(`${API_BASE}/signup/resend_otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username_or_email: username })
      });
      if (resp.ok) {
        setOtpMsg('A new verification code was sent to your email.');
      } else {
        const ed = await resp.json().catch(() => ({}));
        setOtpMsg((ed as any)?.detail || 'Failed to resend code.');
      }
    } catch {
      setOtpMsg('Network error while resending.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSaveApiBase = () => {
    try {
      const trimmed = apiBaseInput.replace(/\/+$/, '');
      localStorage.setItem('API_BASE_OVERRIDE', trimmed);
      // Force reload to ensure new base is used everywhere
      window.location.reload();
    } catch {
      // noop
    }
  };

  const handleClearApiBase = () => {
    try {
      localStorage.removeItem('API_BASE_OVERRIDE');
      window.location.reload();
    } catch {
      // noop
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            CosmaComply
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            MoCRA Compliance Management System
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Release 1
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

          {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

          {otpNeeded && (
            <div className="mt-4 border rounded-md p-3 bg-yellow-50 border-yellow-200">
              <p className="text-sm text-yellow-800 mb-2">{otpMsg || 'Enter the verification code sent to your email.'}</p>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading}
                  className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md disabled:opacity-50"
                >
                  {otpLoading ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={otpLoading}
                  className="px-3 py-2 bg-gray-100 text-gray-800 text-sm rounded-md border"
                >
                  Resend
                </button>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                Contact your administrator if you need credentials.
              </p>
            </div>
          </form>
          {/* Advanced: API Base Switcher (hidden by default on production) */}
          {(!isProd || showAdvanced) && (
            <div className="mt-6">
              {!isProd && (
                <button
                  type="button"
                  onClick={() => setShowAdvanced(v => !v)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}
                </button>
              )}
              {(!isProd || showAdvanced) && (
                <div className="mt-3 border border-gray-200 rounded-md p-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Backend API Base URL</label>
                  <input
                    type="url"
                    value={apiBaseInput}
                    onChange={(e) => setApiBaseInput(e.target.value)}
                    placeholder="https://your-backend.example.com"
                    className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={handleSaveApiBase}
                      className="px-2 py-1 bg-blue-600 text-white text-xs rounded-md"
                    >
                      Save & Reload
                    </button>
                    <button
                      type="button"
                      onClick={handleClearApiBase}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
                    >
                      Clear Override
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] text-gray-500">
                    Current base in use: <span className="font-mono">{API_BASE}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;

